import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ClipboardCheck, BookOpen, Award, CheckCircle, ArrowRight } from 'lucide-react';

const divisions = [
  {
    id: 'accreditation',
    icon: ClipboardCheck,
    title: 'A) Institutional Accreditation Division',
    desc: 'Accredits skill-based programs (not degrees) through comprehensive institutional review.',
    points: [
      'Curriculum audit and validation',
      'Faculty qualification assessment',
      'Infrastructure and facility review',
      'Clinical training adequacy evaluation',
      'Ethics compliance verification',
      'Periodic re-accreditation cycles',
    ],
    link: '/accreditation',
    image: 'https://images.unsplash.com/photo-1758573467240-f944226c2026?auto=format&fit=crop&w=800&q=80',
  },
  {
    id: 'curriculum',
    icon: BookOpen,
    title: 'B) Curriculum & Standards Authority Division',
    desc: 'Develops and maintains the competency frameworks that underpin all IMCAC assessments.',
    points: [
      'Competency framework development',
      'Case log requirement definition',
      'OSCE blueprint publication',
      'Assessment rubric creation',
      'Recertification cycle design',
      'Evidence-based standard updates',
    ],
    link: '/curriculum',
    image: 'https://images.unsplash.com/photo-1758574437877-68a1ec5fafc3?auto=format&fit=crop&w=800&q=80',
  },
  {
    id: 'exam-certification',
    icon: Award,
    title: 'C) Division of Competency-Based Examination & Certification',
    desc: 'Administers all examinations and issues competency-based certification as an independent examination authority.',
    points: [
      'Written examination administration',
      'Full OSCE assessment delivery',
      'Case log verification and review',
      'Viva voce and case defense',
      'Provisional certification issuance',
      'Final certification and credential management',
    ],
    link: '/division-examination-certification',
    image: 'https://images.unsplash.com/photo-1706065264583-55f1a8549769?auto=format&fit=crop&w=800&q=80',
  },
];

export default function DivisionsPage() {
  return (
    <div data-testid="divisions-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Organisational Structure</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Three Core Divisions</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-2xl">
            IMCAC is structured into three interdependent divisions, each responsible for a critical pillar of competency-based certification and professional development.
          </p>
        </div>
      </section>

      {divisions.map((div, i) => (
        <section key={div.id} className={`imcac-section ${i % 2 === 0 ? 'bg-white' : 'bg-[#F8FAFC]'}`} data-testid={`division-${div.id}`}>
          <div className="imcac-container">
            <div className={`grid grid-cols-1 lg:grid-cols-12 gap-12 items-start ${i % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
              <div className={`lg:col-span-7 ${i % 2 !== 0 ? 'lg:order-2' : ''}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#1B2A4A] rounded-sm flex items-center justify-center">
                    <div.icon className="h-5 w-5 text-[#C5A572]" />
                  </div>
                  <h2 className="text-2xl font-semibold text-[#1B2A4A] font-heading">{div.title}</h2>
                </div>
                <p className="text-base text-slate-600 leading-relaxed mb-6">{div.desc}</p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  {div.points.map((point, j) => (
                    <div key={j} className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-700">{point}</span>
                    </div>
                  ))}
                </div>
                <Link to={div.link} data-testid={`${div.id}-learn-more`}>
                  <Button variant="outline" className="rounded-sm border-[#1B2A4A] text-[#1B2A4A]">
                    Explore Division <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              <div className={`lg:col-span-5 ${i % 2 !== 0 ? 'lg:order-1' : ''}`}>
                <img src={div.image} alt={div.title} className="w-full h-72 object-cover rounded-sm shadow-md" />
              </div>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}
